import * as React from 'react';
import {useDispatch} from 'react-redux';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {GroupConfigurationTable} from 'shells/setup/group-configuration-table/group-configuration-table';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {Permission} from 'nutshell-graphql-types';
import type {RoleFragment} from 'nutshell-graphql-types';

import {openModalForType, TitleBarLimits, useGetLimitsByType} from '../../../limits';

import {DISABLED_PERMISSIONS, FIXED_PERMISSIONS, getPermissions, getRoleIcon} from './consts';
import {useCreateRole} from './graphql/hooks/use-create-role';
import {useGetRoles} from './graphql/hooks/use-get-roles';
import {useUpdateRolePermission} from './graphql/hooks/use-update-role-permission';

export function ManageAllRolesPermissionsTable() {
    const [isCreatingRole, setIsCreatingRole] = React.useState(false);

    const dispatch = useDispatch();
    const {roles} = useGetRoles();
    const {createRole, isCreating} = useCreateRole();
    const {updateRolePermission, isUpdating} = useUpdateRolePermission();
    const {limits, refetch: refetchLimits} = useGetLimitsByType('ROLES');

    const permissions = getPermissions();
    const allPermissions = Object.keys(permissions).map((key) => ({
        id: key,
        // @ts-expect-error
        label: permissions[key].label,
    }));

    const getPermissionsForRole = (role: RoleFragment) => {
        // Map over list of active permissions for a role returned from the server
        let permissionsForRole = role.permissions.map((permission) => ({id: permission}));

        // Add any disabled permissions that are not already in the list as these need to be shown as disabled in the UI
        // but will never be returned from the server as they are disabled for all roles (except the Admin role which
        // will be returned with `isImmutable: true` and cannot be modified at all)
        DISABLED_PERMISSIONS.forEach((permission) => {
            if (!role.permissions.includes(permission)) {
                // @ts-expect-error
                permissionsForRole.push({id: permission, isDisabled: true});
            }
        });

        // Update the permissions list for any that include the fixed permission to also include the flag
        // This allows the option to retain it's original state, but not be toggleable
        FIXED_PERMISSIONS.forEach((permission) => {
            if (role.permissions.includes(permission)) {
                permissionsForRole = permissionsForRole.filter((p) => p.id !== permission);
                // @ts-expect-error
                permissionsForRole.push({id: permission, isFixed: true});
            }
        });

        return permissionsForRole;
    };

    return (
        <>
            <GroupConfigurationTable
                title={
                    <StackLegacy spacing={4} isHorizontal={true} alignCenter={true}>
                        <div>Roles &amp; permissions</div>
                        <TitleBarLimits type='ROLES' />
                    </StackLegacy>
                }
                description='This role can…'
                configurations={allPermissions}
                isUpdating={isUpdating}
                groupConfigurations={
                    roles
                        ? roles.map((role) => ({
                              id: role.id,
                              name: role.name,
                              icon: getRoleIcon(role.name),
                              isFixed: role.isImmutable,
                              configurations: getPermissionsForRole(role),
                          }))
                        : []
                }
                onGroupConfigurationChange={(
                    permission: string,
                    roleId: string,
                    isToggled: boolean
                ) => {
                    // Need to pull as Permission type for flow
                    // @ts-expect-error
                    const updatePermission: Permission | null | undefined = Object.keys(
                        permissions
                    ).find((key) => key === permission);
                    if (updatePermission) {
                        updateRolePermission({
                            roleId,
                            permission: updatePermission,
                            action: isToggled ? 'add' : 'remove',
                        });
                    }
                }}
                createNew={
                    // Don't want to show createNew until roles have loaded
                    roles
                        ? {
                              onCreateNew:
                                  limits && !limits.canAdd
                                      ? () => {
                                            dispatch(openModalForType('ROLES'));
                                        }
                                      : () => setIsCreatingRole(true),
                              buttonText: '+ New role',
                          }
                        : undefined
                }
            />
            {isCreatingRole && (
                <SingleInputModal
                    onClose={() => setIsCreatingRole(false)}
                    title='Add a new role'
                    subtitle='Give your role a name'
                    placeholder='Example: Restricted'
                    buttonText='Create role'
                    onSave={(name) => {
                        createRole({name})
                            .then(() => {
                                // Refetch role limits after a new one is created
                                refetchLimits().then(() => {
                                    setIsCreatingRole(false);
                                });
                            })
                            .catch(() => {
                                // Just close out on an error for now
                                setIsCreatingRole(false);
                            });
                    }}
                    isSaving={isCreating}
                />
            )}
        </>
    );
}
